import { useState } from "react";
import { AllowListAddForm } from "@client/components/admin/AllowListAddForm";
import { AllowListTable } from "@client/components/admin/AllowListTable";
import { EmptyState } from "@client/components/EmptyState";
import { TextField } from "@client/components/Field";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { Panel } from "@client/components/Panel";
import { useAllowList } from "@client/hooks/useAdmin";
import { useAuth } from "@client/hooks/useAuth";
import { useDebouncedValue } from "@client/hooks/useDebouncedValue";

/**
 * `/admin/allowlist` — the gate itself.
 *
 * A valid Keesa login is only half of what it takes to use this portal; a live
 * row here is the other half (docs/07-auth.md). That is worth saying on the page,
 * because "add them to the allow list" and "create them an account" are not the
 * same job and an administrator arriving here may be looking for the second one.
 */
export function AdminAllowListPage() {
  const auth = useAuth();
  const [search, setSearch] = useState("");
  const [includeRevoked, setIncludeRevoked] = useState(false);

  const debouncedSearch = useDebouncedValue(search);
  const filters = {
    q: debouncedSearch.trim() || undefined,
    includeRevoked: includeRevoked || undefined,
  };
  const allowList = useAllowList(filters);

  const items = allowList.data?.items ?? [];
  const filtered = Boolean(filters.q);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-ink">Allow list</h1>
        <p className="max-w-prose text-sm text-ink-soft">
          Signing in needs two things: a working account with the identity provider, and an address
          on this list. Adding someone here does not create their account — it lets the account they
          already have reach this portal.
        </p>
      </header>

      <Panel title="Add people" description="They can sign in as soon as they are on the list.">
        <AllowListAddForm />
      </Panel>

      <Panel
        title="Who has access"
        description="Roles take effect on their next request, not at their next sign-in."
        actions={
          <label className="flex items-center gap-2 text-xs font-semibold text-ink">
            <input
              type="checkbox"
              checked={includeRevoked}
              onChange={(event) => setIncludeRevoked(event.target.checked)}
              className="h-4 w-4 accent-[var(--azure-deep)]"
            />
            Show revoked
          </label>
        }
      >
        <div className="max-w-sm">
          <TextField
            label="Search"
            type="search"
            placeholder="Email or note"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {allowList.isPending && <LoadingBlock rows={4} label="Loading the allow list" />}

        {allowList.isError && (
          <ErrorNotice error={allowList.error} onRetry={() => void allowList.refetch()} />
        )}

        {allowList.isSuccess && items.length === 0 && (
          <EmptyState
            title={filtered ? "No addresses match that search" : "Nobody is on the list yet"}
            description={
              filtered
                ? "Nothing here answers to that search. Revoked entries are hidden unless you ask for them."
                : "Until an address is added here, a valid sign-in is still refused. Add the first one above."
            }
          />
        )}

        {items.length > 0 && <AllowListTable rows={items} currentEmail={auth.user?.email ?? ""} />}

        {allowList.data && allowList.data.total > items.length && (
          <p className="text-xs text-ink-soft">
            Showing {items.length} of {allowList.data.total}. Narrow the search to see the rest.
          </p>
        )}
      </Panel>
    </div>
  );
}
